import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Clock,
  AlertTriangle,
  X,
} from 'lucide-react';
import { credits, CreditTransaction } from '../../api/client';
import { formatNumber, cn } from '../../lib/utils';

const PAGE_SIZE = 50;

export default function CreditsGenerations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [activeReservations, setActiveReservations] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deviceFilter, setDeviceFilter] = useState(searchParams.get('device_id') || '');
  const [expandedTx, setExpandedTx] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await credits.generations({
        type: typeFilter !== 'all' ? typeFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        device_id: deviceFilter.trim() || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setTransactions(res.transactions);
      setTotal(res.total);
      setActiveReservations(res.active_reservations);
    } catch (error) {
      console.error('Failed to fetch generations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, typeFilter, statusFilter, deviceFilter]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading generations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Generations</h1>
          <p className="text-muted-foreground mt-1">Credit transaction history ({formatNumber(total)} total)</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap animate-fade-in-up animate-delay-100">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Device ID</label>
          <div className="relative">
            <input
              type="text"
              value={deviceFilter}
              onChange={(e) => { setDeviceFilter(e.target.value); setPage(0); }}
              placeholder="Filter by device ID..."
              className="px-3 py-2 rounded-xl bg-secondary border border-border/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 w-72 pr-8"
            />
            {deviceFilter && (
              <button
                onClick={() => { setDeviceFilter(''); setPage(0); setSearchParams({}); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Type</label>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 rounded-xl bg-secondary border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          >
            <option value="all">All types</option>
            <option value="text">Text</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 rounded-xl bg-secondary border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          >
            <option value="all">All statuses</option>
            <option value="debit">Debit</option>
            <option value="reserve">Reserve</option>
            <option value="refund">Refund</option>
            <option value="grant">Grant</option>
            <option value="purchase">Purchase</option>
            <option value="free_chat">Free Chat</option>
            <option value="subscriber">Subscriber</option>
          </select>
        </div>
      </div>

      {/* Active Reservations Warning */}
      {activeReservations > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center gap-3 animate-fade-in-up animate-delay-200">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-300">
            <span className="font-semibold">{activeReservations} active reservation{activeReservations > 1 ? 's' : ''}</span> — not yet confirmed or refunded
          </span>
        </div>
      )}

      {/* Transactions Table */}
      <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up animate-delay-200">
        <table className="table-neural min-w-full">
          <thead>
            <tr>
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-8" />
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">ID</th>
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Device</th>
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
              <th className="px-4 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Credits</th>
              <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="px-4 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx) => (
              <TxRow
                key={tx.id}
                tx={tx}
                isExpanded={expandedTx === tx.id}
                onToggle={() => setExpandedTx(expandedTx === tx.id ? null : tx.id)}
              />
            ))}
            {transactions.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground">No transactions found</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between animate-fade-in-up animate-delay-300">
          <p className="text-sm text-muted-foreground">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className={cn(
                'p-2 rounded-lg bg-secondary text-muted-foreground transition-colors',
                page === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:text-foreground'
              )}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-muted-foreground px-2">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className={cn(
                'p-2 rounded-lg bg-secondary text-muted-foreground transition-colors',
                page >= totalPages - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:text-foreground'
              )}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TxRow({
  tx,
  isExpanded,
  onToggle,
}: {
  tx: CreditTransaction;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const statusStyles: Record<string, string> = {
    grant: 'bg-emerald-500/15 text-emerald-400',
    purchase: 'bg-teal-500/15 text-teal-400',
    debit: 'bg-amber-500/15 text-amber-400',
    reserve: 'bg-violet-500/15 text-violet-400',
    refund: 'bg-cyan-500/15 text-cyan-400',
    free_chat: 'bg-sky-500/15 text-sky-400',
    subscriber: 'bg-fuchsia-500/15 text-fuchsia-400',
  };

  return (
    <>
      <tr className="cursor-pointer hover:bg-secondary/50 transition-colors" onClick={onToggle}>
        <td className="px-4 py-3">
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-emerald-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          )}
        </td>
        <td className="px-4 py-3">
          <code className="text-xs text-muted-foreground font-mono">{tx.id.slice(0, 8)}</code>
        </td>
        <td className="px-4 py-3">
          <code className="text-xs text-muted-foreground font-mono">
            {tx.device_id.slice(0, 8)}...{tx.device_id.slice(-4)}
          </code>
        </td>
        <td className="px-4 py-3">
          <span className="text-xs font-medium">{tx.gen_type || '—'}</span>
        </td>
        <td className="px-4 py-3 text-right">
          <span className={cn(
            'font-mono text-sm font-medium',
            tx.amount > 0 ? 'text-emerald-400' : tx.amount === 0 ? 'text-muted-foreground' : 'text-rose-400'
          )}>
            {tx.amount > 0 ? '+' : ''}{tx.amount}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className={cn('px-2 py-0.5 rounded text-[10px] font-medium uppercase', statusStyles[tx.type] || 'bg-secondary text-muted-foreground')}>
            {tx.type}
          </span>
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            {new Date(tx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="px-6 py-0">
            <div className="py-4 pl-8 border-l-2 border-emerald-500/30 ml-2 grid grid-cols-2 md:grid-cols-4 gap-4">
              <DetailItem label="Full ID" value={tx.id} mono />
              <DetailItem label="Device" value={tx.device_id} mono />
              <DetailItem label="Gen Type" value={tx.gen_type || '—'} />
              <DetailItem label="Credits" value={`${tx.amount > 0 ? '+' : ''}${tx.amount}`} />
              <DetailItem label="Balance After" value={String(tx.balance_after)} />
              <DetailItem label="Job ID" value={tx.gen_job_id || '—'} mono />
              <DetailItem label="Reason" value={tx.reason || '—'} />
              <DetailItem label="Note" value={tx.admin_note || '—'} />
              <DetailItem label="Created" value={new Date(tx.created_at).toLocaleString()} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={cn('text-sm break-all', mono ? 'font-mono text-xs' : '')}>{value}</p>
    </div>
  );
}
